import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { useLang } from '../context/LangContext';
import { getPostBySlug, getPosts } from '../data/blogPosts';
import { getServiceBySlug } from '../data/servicesData';
import { getTeamMemberBySlug } from '../data/teamData';
import Blog1 from '../assets/images/Blog1.jpg';
import blog2 from '../assets/images/blog2.jpg';
import blog3 from '../assets/images/blog3.jpg';
import blog4 from '../assets/images/blog4.jpg';
import blog6 from '../assets/images/blog6.jpg';
import picosureImg from '../assets/images/picosure.jpg';
import pigmentationImg from '../assets/images/pigmentation-guide.jpg';
import potenzaCoverImg from '../assets/images/potenza-microneedling.jpg';
import blog9 from '../assets/images/blog9.png';

const coverBySlug = {
  'beginner-medical-aesthetics': Blog1,
  'skinbooster-cocktail': blog2,
  'hyaluronic-acid-filler': blog2,
  'skin-care-right-way': blog3,
  'post-treatment-care-tips': blog4,
  'radiesse': Blog1,
  'plla-dilution-and-applications': Blog1,
  'sculptra-vs-radiesse': blog4,
  'ipl-vs-picosecond': picosureImg,
  'botox-dilution-and-concentration': blog6,
  'sun-spots-melasma-post-acne-marks': pigmentationImg,
  'potenza-vs-traditional-microneedling': potenzaCoverImg,
  'acne-scars-vs-acne-marks': blog9,
};

const fallbackServiceMap = {
  'ipl-vs-picosecond': 'picosure-pro',
  'sculptra-vs-radiesse': 'sculptra',
  'plla-dilution-and-applications': 'sculptra',
  'hyaluronic-acid-filler': 'dermal-fillers',
  'radiesse': 'radiesse',
  'skinbooster-cocktail': 'mesotherapy',
  'skin-care-right-way': 'hydrafacial',
  'post-treatment-care-tips': 'picosure-pro',
  'beginner-medical-aesthetics': 'potenza-rf-microneedling',
  'botox-dilution-and-concentration': 'botox-neurotoxin',
  'sun-spots-melasma-post-acne-marks': 'picosure-pro',
  'potenza-vs-traditional-microneedling': 'potenza-rf-microneedling',
  'acne-scars-vs-acne-marks': 'potenza-rf-microneedling',
};

export const coverAltBySlug = {
  en: {
    'ipl-vs-picosecond': 'Clinical comparison of IPL photorejuvenation vs 755nm picosecond laser for Asian skin hyperpigmentation',
    'sculptra-vs-radiesse': 'Comparison diagram of Sculptra PLLA biostimulator vs Radiesse CaHA injectable treatment',
    'plla-dilution-and-applications': 'PLLA collagen stimulation treatment dilution ratio and facial application diagram',
    'skinbooster-cocktail': 'Skin booster cocktail therapy formulation and multi-ingredient skin hydration treatment',
    'radiesse': 'Calcium Hydroxylapatite (CaHA) mesotherapy skin booster injection technique and collagen regeneration',
    'skin-care-right-way': 'Professional medical aesthetic skin care routine and post-treatment maintenance tips',
    'hyaluronic-acid-filler': 'Hyaluronic Acid Filler facial volumizing, dermal contouring, and wrinkle reduction treatment',
    'post-treatment-care-tips': 'Post-aesthetic treatment skin care recovery guidelines and protective measures',
    'beginner-medical-aesthetics': 'Beginner guide to medical aesthetic treatment selection and consultation',
    'botox-dilution-and-concentration': 'Botox reconstitution dilution ratio and botulinum toxin clinical concentration guide for facial aesthetic injections',
    'sun-spots-melasma-post-acne-marks': 'Clinical comparison and pigmentation assessment of sun spots, melasma, and post-acne marks (PIH/PIE)',
    'potenza-vs-traditional-microneedling': 'Potenza radiofrequency microneedling vs traditional microneedling mechanism, Tiger Tip and Fusion Tip comparison',
    'acne-scars-vs-acne-marks': 'Clinical assessment guide for acne marks (PIE/PIH) vs true acne scars, scar subtypes, and consultation options',
  },
  cn: {
    'ipl-vs-picosecond': '亚洲皮肤色素沉淀与痘印管理：光子嫩肤 (IPL) 与 755nm 蜂巢皮秒激光临床选择与波长靶点解析',
    'sculptra-vs-radiesse': '童颜针 (PLLA) 与 微晶瓷 (CaHA) 胶原再生机制、组织支撑及临床选法对比图',
    'plla-dilution-and-applications': '童颜针 (PLLA) 胶原蛋白再生配比与面部打法结构解析图',
    'skinbooster-cocktail': '水光针多成分复配方案与皮肤深层补水紧致效果图',
    'radiesse': '微晶瓷 (CaHA) 水光复配稀释打法与胶原刺激应用示意图',
    'skin-care-right-way': '专业医美级日常皮肤护理与术后维持流程指南图',
    'hyaluronic-acid-filler': '玻尿酸面部填充塑形与抚平皱纹治疗成果展示图',
    'post-treatment-care-tips': '医美项目术后修复护理注意事项与恢复期指南',
    'beginner-medical-aesthetics': '新手医美入门项目选择与专业医师面诊咨询示意图',
    'botox-dilution-and-concentration': '肉毒素不同稀释比例与浓度配置临床对比解析及面部除皱微滴注射示意图',
    'sun-spots-melasma-post-acne-marks': '日晒斑、黄褐斑与痘印（PIH/PIE）临床鉴别与色素评估专业解析图',
    'potenza-vs-traditional-microneedling': 'Potenza 黄金射频微针与普通微针对比、Tiger Tip 半绝缘及 Fusion Tip 气压深导技术图',
    'acne-scars-vs-acne-marks': '痘疤与痘印（PIE/PIH）临床区别、冰锥/箱车/滚动型痘坑与医美面诊方案解析',
  }
};

export function getCoverImageAlt(post, lang) {
  if (!post) return 'Halo Aesthetic Clinic medical aesthetics treatment';
  const language = lang || post.language || 'en';
  const altMap = coverAltBySlug[language] || coverAltBySlug.en;
  if (altMap[post.slug]) return altMap[post.slug];
  return `${post.title} - ${post.category || 'Medical Aesthetics'} ${language === 'cn' ? '展示图' : 'illustration'}`;
}

function formatAbsoluteImageUrl(imgSrc) {
  if (!imgSrc) return 'https://www.haloaestheticclinic.com/logo.jpg';
  if (typeof imgSrc === 'string' && (imgSrc.startsWith('http://') || imgSrc.startsWith('https://'))) {
    return imgSrc;
  }
  const cleanPath = typeof imgSrc === 'string' && imgSrc.startsWith('/') ? imgSrc : `/${imgSrc}`;
  return `https://www.haloaestheticclinic.com${cleanPath}`;
}

/** Data layer: load post by slug, derive related posts and tag list. */
function useBlogDetail(slug) {
  const { lang } = useLang();
  const post = slug ? getPostBySlug(lang, slug) : null;
  const notFound = !post;
  const coverImage = post ? (coverBySlug[post.slug] || post.coverImage) : null;
  const allPosts = getPosts(lang);
  const relatedPosts = post
    ? allPosts.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 2)
    : [];
  const tagList = post
    ? (Array.isArray(post.tags) ? post.tags : post.tags ? [post.tags] : [])
    : [];
  
  const relatedServiceSlug = post?.relatedService || (post?.slug ? fallbackServiceMap[post.slug] : null);
  const relatedServiceData = relatedServiceSlug ? getServiceBySlug(relatedServiceSlug) : null;

  return { post, notFound, coverImage, relatedPosts, tagList, relatedServiceSlug, relatedServiceData };
}

/** Build Article JSON-LD for blog detail (SEO / E-E-A-T). */
function buildArticleSchema(post, coverImageUrl, canonicalUrl) {
  const tagList = Array.isArray(post.tags) ? post.tags : post.tags ? [post.tags] : [];
  const inLanguage = post.language === 'cn' ? 'zh-CN' : 'en-US';

  const authorKey = post.authorPerson || post.author || 'Lydia';
  const authorMember = getTeamMemberBySlug(authorKey);

  const authorSchema = authorMember
    ? {
        '@type': 'Person',
        name: authorMember.name.en || 'Lydia',
        jobTitle: authorMember.title.en || 'Chief Nurse Manager & Lead Injector',
        url: `https://www.haloaestheticclinic.com/team/${authorMember.slug}`,
      }
    : {
        '@type': 'Organization',
        name: post.author || 'Halo Aesthetic Clinic',
        url: 'https://www.haloaestheticclinic.com',
      };

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${canonicalUrl}#article`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    author: authorSchema,
    publisher: {
      '@type': 'Organization',
      name: post.author || 'Halo Aesthetic Clinic',
      url: 'https://www.haloaestheticclinic.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.haloaestheticclinic.com/logo.jpg',
      },
    },
    image: coverImageUrl,
    keywords: tagList.join(', '),
    inLanguage,
  };
}

/** UI-only strings (no blog post content) */
const content = {
  en: {
    backToBlog: '← Back to Blog',
    author: 'Author',
    category: 'Category',
    publishedDate: 'Published',
    relatedPosts: 'Related Posts',
    tags: 'Tags',
    noPostFound: 'Blog post not found',
    goBack: 'Go Back to Blog',
    relatedServiceHeading: 'Featured Treatment',
    learnMoreServicePrefix: 'Learn more about',
    learnMoreServiceSuffix: 'details',
    ctaBadge: 'Halo Medical Aesthetic Consultation',
    ctaTitle: 'Have questions about this treatment?',
    ctaSubtitle: 'Book a 1-on-1 consultation with our licensed nurse to receive a customized treatment plan.',
    bookOnlineNow: 'Book Online Now',
    callConsultation: 'Call Consultation: (942) 388-1688',
    bookOnlineAria: 'Book online consultation with a licensed nurse',
    callPhoneAria: 'Call Halo Aesthetic Clinic for consultation at (942) 388-1688',
    viewServiceAria: 'View details for service',
  },
  cn: {
    backToBlog: '← 返回博客',
    author: '作者',
    category: '分类',
    publishedDate: '发布时间',
    relatedPosts: '相关文章',
    tags: '标签',
    noPostFound: '未找到博客文章',
    goBack: '返回博客列表',
    relatedServiceHeading: '推荐关联项目',
    learnMoreServicePrefix: '了解更多关于',
    learnMoreServiceSuffix: '详情',
    ctaBadge: 'Halo 医美专业面诊预约',
    ctaTitle: '对该疗程有疑问？',
    ctaSubtitle: '预约我们的专业持牌护士进行一对一面诊，为您定制专属个性化方案。',
    bookOnlineNow: '立即在线预约',
    callConsultation: '电话咨询：(942) 388-1688',
    bookOnlineAria: '预约我们的专业持牌护士进行一对一面诊',
    callPhoneAria: '拨打电话咨询 (942) 388-1688',
    viewServiceAria: '查看关联项目详情',
  }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const c = content[lang];
  const { post, notFound, coverImage, relatedPosts, tagList, relatedServiceSlug, relatedServiceData } = useBlogDetail(slug);

  if (notFound) {
    return (
      <div className="bg-softGradient bg-no-repeat bg-top text-text">
        <Helmet>
          <title>{c.noPostFound} | Halo Aesthetic Clinic</title>
          <meta name="description" content="The requested blog post could not be found." />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-section">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.noPostFound}</h1>
            <Link
              to="/blog"
              className="inline-block px-6 py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
              }}
            >
              {c.goBack}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://www.haloaestheticclinic.com/blog/${post.slug}`;
  const absoluteCoverImageUrl = formatAbsoluteImageUrl(coverImage || post.coverImage);
  const articleSchema = buildArticleSchema(post, absoluteCoverImageUrl, canonicalUrl);
  const descriptiveAlt = getCoverImageAlt(post, lang);

  const localizedServiceTitle = relatedServiceData
    ? (relatedServiceData[lang]?.title || relatedServiceData.en?.title || '')
    : '';

  const authorKey = post.authorPerson || post.author || 'Lydia';
  const authorMember = getTeamMemberBySlug(authorKey);

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{post.title} | Halo Aesthetic Clinic</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${post.title} | Halo Aesthetic Clinic`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={absoluteCoverImageUrl} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-section">
        <div className="mb-8">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center text-[#2E6B4C] hover:text-[#2E6B4C]/80 font-semibold transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {c.backToBlog}
          </button>
        </div>

        <article className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card border border-champagne/40 overflow-hidden">
          <div className="relative">
            <img
              src={coverImage}
              alt={descriptiveAlt}
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute top-4 left-4">
              <span className="bg-[#2E6B4C]/90 text-white px-3 py-1 rounded-xl text-sm font-semibold">
                {post.category}
              </span>
            </div>
          </div>

          <div className="p-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 leading-tight">
              {post.title}
            </h1>

            {post.editorialNote && (
              <div className="text-sm text-gray-600 mb-6 italic border-l-2 border-champagne/60 pl-4 [&>p]:m-0">
                <ReactMarkdown>{post.editorialNote}</ReactMarkdown>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-6 mb-8 pb-6 border-b border-champagne/40">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-[#2E6B4C] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-gray-700">
                  <span className="font-semibold">{c.author}:</span>{' '}
                  {authorMember && authorMember.hasDetailPage ? (
                    <Link
                      to={`/team/${authorMember.slug}`}
                      aria-label={`View ${authorMember.name[lang] || authorMember.name.en}'s medical profile`}
                      className="text-[#2E6B4C] font-semibold hover:underline inline-flex items-center gap-1 group"
                    >
                      <span>{post.authorPerson || authorMember.name[lang] || authorMember.name.en}</span>
                      <span className="text-xs text-gray-500 font-normal">
                        ({authorMember.title[lang] || authorMember.title.en})
                      </span>
                      <svg className="w-3.5 h-3.5 text-[#2E6B4C] transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </Link>
                  ) : (
                    <Link
                      to="/about"
                      className="text-[#2E6B4C] font-semibold hover:underline"
                    >
                      {post.authorPerson || post.author || 'Halo Aesthetic Clinic'}
                    </Link>
                  )}
                </span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-[#2E6B4C] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-700">
                  <span className="font-semibold">{c.publishedDate}:</span> {post.date}
                </span>
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed prose-headings:text-gray-900 prose-strong:text-gray-900 prose-p:prose-p">

              <ReactMarkdown
                components={{
                  img: ({ node, src, alt, ...props }) => (
                    <img
                      src={src}
                      alt={alt && alt.length > 3 ? alt : `${post.title} - ${post.category} illustration`}
                      className="rounded-xl shadow-card my-6 max-w-full h-auto"
                      {...props}
                    />
                  )
                }}
              >
                {post.body}
              </ReactMarkdown>
            </div>

            {/* Service Cross-link Recommendation Card */}
            {relatedServiceData && relatedServiceSlug && (
              <div className="mt-10 p-6 bg-gradient-to-r from-amber-50/80 via-white to-amber-50/80 rounded-2xl border border-champagne/60 shadow-soft">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  {relatedServiceData.image && (
                    <img
                      src={relatedServiceData.image}
                      alt={localizedServiceTitle}
                      className="w-full sm:w-36 h-28 object-cover rounded-xl shadow-sm border border-champagne/30"
                    />
                  )}
                  <div className="flex-1 text-center sm:text-left">
                    <span className="inline-block text-xs font-bold tracking-wider text-[#2E6B4C] uppercase mb-1">
                      {c.relatedServiceHeading}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {localizedServiceTitle}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {relatedServiceData[lang]?.subtitle || relatedServiceData.en?.subtitle || ''}
                    </p>
                    <Link
                      to={`/services/${relatedServiceSlug}`}
                      aria-label={`${c.viewServiceAria}: ${localizedServiceTitle}`}
                      className="inline-flex items-center text-sm font-bold text-[#2E6B4C] hover:text-[#2E6B4C]/80 hover:underline transition-all group"
                    >
                      <span>
                        {lang === 'cn'
                          ? `了解更多关于${localizedServiceTitle}详情`
                          : `Learn more about ${localizedServiceTitle}`}
                      </span>
                      <svg
                        className="w-4 h-4 ml-1.5 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {tagList.length > 0 && (
              <div className="mt-8 pt-6 border-t border-champagne/40">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{c.tags}:</h3>
                <div className="flex flex-wrap gap-2">
                  {tagList.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-white/60 backdrop-blur-md border border-sage/50 text-gray-700 rounded-xl text-sm hover:shadow-glow transition-all"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Post-Article Consultation CTA Card */}
        <section className="mt-10 mb-12 p-8 md:p-10 bg-gradient-to-r from-[#2E6B4C]/10 via-[#D9C3A9]/25 to-[#2E6B4C]/10 backdrop-blur-md rounded-2xl border border-champagne/60 shadow-card text-center relative overflow-hidden">
          <div className="max-w-2xl mx-auto relative z-10">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-white/90 rounded-full border border-champagne/50 text-xs font-semibold text-[#2E6B4C] mb-4 shadow-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>{c.ctaBadge}</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-snug">
              {c.ctaTitle}
            </h3>
            <p className="text-gray-700 text-base md:text-lg mb-8 leading-relaxed">
              {c.ctaSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/booking"
                aria-label={c.bookOnlineAria}
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-bold text-gray-900 shadow-soft hover:shadow-glow hover:brightness-105 transition-all text-center border border-champagne/60 flex items-center justify-center gap-2"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.85) 50%, rgba(217,195,169,0.7) 100%)'
                }}
              >
                <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{c.bookOnlineNow}</span>
              </Link>
              <a
                href="tel:942-388-1688"
                aria-label={c.callPhoneAria}
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-bold text-white bg-[#2E6B4C] hover:bg-[#2E6B4C]/90 shadow-md hover:shadow-glow transition-all text-center flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>{c.callConsultation}</span>
              </a>
            </div>
          </div>
        </section>

        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{c.relatedPosts}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card overflow-hidden border border-champagne/40 hover:shadow-glow transition-all"
                >
                  <img
                    src={coverBySlug[relatedPost.slug] || relatedPost.coverImage}
                    alt={getCoverImageAlt(relatedPost, lang)}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <div className="text-sm text-gray-600">
                      <span>{relatedPost.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;

